'use client';

import React, { useEffect, useState } from 'react';
import Image from 'next/image';
import { useLocale } from 'next-intl';
import ourClientsEllipseMap from '@/assets/illustrations/our-clients-ellipse-map.svg';
import mapMark from '@/assets/icons/map-mark.svg';
import { OurClientsData } from '@/types/ourClients';
import { studioClient } from '@/sanity/lib/client';
import { ourClientsQuery } from '@/sanity/queries/ourClients';
import { LoadingSpinner } from '@/shared';

export default function OurClients() {
  const locale = useLocale();
  const [ourClientsData, setOurClientsData] = useState<OurClientsData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOurClientsData = async () => {
      try {
        const data = await studioClient.fetch<OurClientsData>(ourClientsQuery);
        setOurClientsData(data);
      } catch (error) {
        console.error('Error fetching our clients data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchOurClientsData();
  }, []);

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!ourClientsData) {
    return null;
  }

  const title =
    ourClientsData.title[locale as keyof typeof ourClientsData.title] || ourClientsData.title.en;
  const description =
    ourClientsData.description[locale as keyof typeof ourClientsData.description] ||
    ourClientsData.description.en;
  const testimonialText =
    ourClientsData.testimonialText[locale as keyof typeof ourClientsData.testimonialText] ||
    ourClientsData.testimonialText.en;

  return (
    <section className="relative my-8 h-[400px] w-full md:my-16 md:h-[662px]">
      <div className="absolute inset-0 h-[420px] md:h-[682px]">
        <Image
          src={ourClientsEllipseMap}
          alt="our-clients-ellipse-map"
          fill
          className="object-cover"
        />

        <div className="absolute inset-0 bg-gradient-to-b from-black to-transparent" />
      </div>

      <div className="relative z-10 py-6 text-center md:py-10">
        <p className="text-hero-gradient-text text-3xl font-bold md:text-5xl">{title}</p>
        <p className="mx-auto max-w-[763px] px-4 text-lg text-white/90 md:px-0 md:text-2xl">
          {description}
        </p>
      </div>

      {/* Map marks with client images inside - Hidden on mobile */}
      <div className="hidden md:block">
        {ourClientsData.clients.map((client) => (
          <div
            key={client._id}
            className="group absolute z-10"
            style={{
              left: `${client.position.left}%`,
              bottom: `${client.position.bottom}%`,
            }}>
            <div className="relative">
              <Image src={mapMark} alt="map-mark" className="w-auto" />
              <div className="absolute inset-0 bottom-12 flex items-center justify-center">
                <Image
                  src={client.logo.asset?.url || ''}
                  alt={client.logo.alt || ''}
                  width={93}
                  height={300}
                  className="rounded-full object-contain"
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Content container with glassmorphism effect */}
      <div className="absolute bottom-0 left-1/2 w-full -translate-x-1/2 transform p-3 lg:w-fit">
        <div
          className="w-full rounded-[18px] bg-white/5 p-4 text-center backdrop-blur-[13px] md:mx-0 md:w-auto md:p-6"
          style={{
            borderRadius: '18px',
            background: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(13px)',
          }}>
          <div className="flex w-full flex-col items-center justify-center space-y-2 text-sm text-white md:max-w-[643px] md:space-y-4 md:text-lg">
            <p className="text-center">{testimonialText}</p>
          </div>
        </div>
      </div>
    </section>
  );
}
